/**
 * RegionalDirectorDashboard — landing page for the `regional_director` role.
 *
 * Two views, toggled by `view` state:
 *   - "list":    search clients + grid of recent proposals
 *   - "builder": create/edit a single proposal
 *
 * Built as a single file so it ships cohesively. If it grows past ~800 lines
 * we'll split builder into its own component.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  Plus,
  Search,
  ExternalLink,
  Copy,
  Eye,
  Trash2,
  Pencil,
  Loader2,
  Building2,
  ArrowLeft,
  X,
  Check,
  Clock,
  LinkIcon,
  BarChart3,
  Upload,
  Film,
  Users,
  Crown,
  Info,
  LogOut,
  Lock,
  Camera,
} from "lucide-react";
import RoiSettingsCard from "./RoiSettingsCard";
import ProposalOrderPanel from "./ProposalOrderPanel";
import RddToggle from "./RddToggle";
import { LinkExpiryChip, RefreshLinkButton } from "./ProposalLinkExpiry";
import { productKey, bundleKey, resolveLayoutOrder, DEFAULT_SMALL_PRINT } from "./proposalLayout";
import { AppHeader } from "@/AppHeader";
import ProposalAnalyticsModal from "@/ProposalAnalyticsModal";
import ProspectsMap from "@/ProspectsMap";
import { useAccess } from "@/useAccess";
import { useAuth } from "@/AuthContext";
import "./RegionalDirectorDashboard.css";

const API = process.env.REACT_APP_BACKEND_URL;

function DashboardTabs({ activeTab, onTabChange, showProspects, allowNewBusiness, showSocialPitch = true }) {
  return (
    <div className="rdd-tabs" data-testid="rdd-tabs">
      {showSocialPitch && (
      <button
        className={`rdd-tab ${activeTab === "social" ? "is-active" : ""}`}
        onClick={() => onTabChange("social")}
        data-testid="rdd-tab-social"
      >
        Existing client · Social pitch
      </button>
      )}
      <button
        className={`rdd-tab ${activeTab === "new_business" ? "is-active" : ""} ${allowNewBusiness ? "" : "is-locked"}`}
        onClick={() => allowNewBusiness && onTabChange("new_business")}
        disabled={!allowNewBusiness}
        title={allowNewBusiness ? undefined : "Full proposals are coming soon for your account"}
        data-testid="rdd-tab-new-biz"
      >
        New business · Full proposal
        {!allowNewBusiness && <Lock size={11} className="rdd-tab-lock" />}
      </button>
      {showProspects && (
        <button
          className={`rdd-tab ${activeTab === "prospects" ? "is-active" : ""}`}
          onClick={() => onTabChange("prospects")}
          data-testid="rdd-tab-prospects"
        >
          Prospects Map
        </button>
      )}
    </div>
  );
}

// Pre-ticked on every new New Business proposal (recommended order: Photography → Floorplans → EPC)
const DEFAULT_NEW_BUSINESS_PRODUCTS = ["photos-standard", "floorplan-standard", "epc-residential"];

const DEFAULTS = {
  social_go_price: "£50",
  social_pro_price: "£60",
  narratour_price: "+£15",
  presentour_price: "+£30",
};

const fmtRelative = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

export default function RegionalDirectorDashboard({ onLogout, onBack }) {
  const { user } = useAuth();
  const [view, setView] = useState("list");
  const [activeTab, setActiveTab] = useState("social"); // "social" | "new_business" | "prospects"
  // Roles & Access decides which sections this person sees (Admin → Users & Roles → Roles & Access)
  const { can, loading: accessLoading } = useAccess();
  const showSocialPitch = can("proposals.social_pitch");
  const showProspects = can("proposals.prospects");
  const allowNewBusiness = can("proposals.new_business");
  useEffect(() => {
    if (accessLoading) return;
    if (activeTab === "social" && !showSocialPitch) setActiveTab(allowNewBusiness ? "new_business" : showProspects ? "prospects" : "social");
  }, [accessLoading, showSocialPitch, allowNewBusiness, showProspects, activeTab]);
  const [editingId, setEditingId] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [builderType, setBuilderType] = useState("social_pitch");

  const openBuilder = (proposalId = null, client = null, type = null) => {
    setEditingId(proposalId);
    setEditingClient(client);
    setBuilderType(type || (activeTab === "new_business" ? "new_business" : "social_pitch"));
    setView("builder");
  };
  const closeBuilder = () => {
    setView("list");
    setEditingId(null);
    setEditingClient(null);
  };

  return (
    <div className="rdd-page" data-testid="rd-dashboard">
      <Toaster position="top-right" richColors />
      <AppHeader onBack={onBack} />

      {view === "list" ? (
        <ProposalsList
          onOpenBuilder={openBuilder}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          showProspects={showProspects}
          allowNewBusiness={allowNewBusiness}
          showSocialPitch={showSocialPitch}
          showAnalytics={can("proposals.analytics")}
        />
      ) : (
        <ProposalBuilder
          proposalId={editingId}
          presetClient={editingClient}
          builderType={builderType}
          onClose={closeBuilder}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   LIST VIEW
   ───────────────────────────────────────────────────────────── */
function ProposalsList({ onOpenBuilder, activeTab, onTabChange, showProspects, allowNewBusiness, showSocialPitch = true, showAnalytics = true }) {
  const { user } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [clients, setClients] = useState([]);
  const [searchingClients, setSearchingClients] = useState(false);
  const [analyticsId, setAnalyticsId] = useState(null);
  const [shareProposal, setShareProposal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/proposals`);
      setProposals(r.data.proposals || []);
    } catch (err) {
      const status = err.response?.status;
      // Auth header can lag the first render — retry once quietly instead of toasting.
      if (status === 401 || status === 403) {
        setTimeout(async () => {
          try {
            const r2 = await axios.get(`${API}/api/proposals`);
            setProposals(r2.data.proposals || []);
          } catch (_) {}
        }, 600);
      } else {
        toast.error(err.response?.data?.detail || "Could not load proposals");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Debounced client search
  useEffect(() => {
    if (!search.trim()) {
      setClients([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearchingClients(true);
      try {
        const r = await axios.get(`${API}/api/proposals/clients/search`, {
          params: { q: search.trim() },
        });
        setClients(r.data.clients || []);
      } catch (err) {
        // Silent — search failures aren't user-actionable
        console.error("client search failed", err);
      } finally {
        setSearchingClients(false);
      }
    }, 280);
    return () => clearTimeout(t);
  }, [search]);

  const filteredProposals = useMemo(() => {
    const tabType = activeTab === "new_business" ? "new_business" : "social_pitch";
    const byTab = proposals.filter((p) =>
      (p.proposal_type || "social_pitch") === tabType
    );
    if (!search.trim()) return byTab;
    const q = search.toLowerCase();
    return byTab.filter((p) =>
      (p.agency_name || "").toLowerCase().includes(q)
    );
  }, [proposals, search, activeTab]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this proposal? The shared link will stop working.")) return;
    try {
      await axios.delete(`${API}/api/proposals/${id}`);
      toast.success("Proposal deleted");
      setProposals((arr) => arr.filter((p) => p.id !== id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const copyLink = (slug) => {
    const url = `${window.location.origin}/proposal/${slug}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Link copied"),
      () => toast.error("Could not copy")
    );
  };

  return (
    <div className="rdd-wrap">
      {/* ── Tabs ── */}
      <DashboardTabs activeTab={activeTab} onTabChange={onTabChange} showProspects={showProspects} allowNewBusiness={allowNewBusiness} showSocialPitch={showSocialPitch} />

      {activeTab === "prospects" ? (
        <ProspectsMap />
      ) : (
      <>
      <div className="rdd-title-bar">
        <div>
          <h1 className="rdd-title">
            {activeTab === "new_business" ? "New Business Proposals" : "Pricing Proposals"}
          </h1>
          <p className="rdd-subtitle">
            {activeTab === "new_business"
              ? "Build a full presentation deck for a prospective agency — services, custom bundles and pricing."
              : "Build bespoke Social Go / Social Pro pages for existing clients — share by link."}
          </p>
        </div>
        <button
          className="rdd-new-btn"
          onClick={() => onOpenBuilder(null, null, activeTab === "new_business" ? "new_business" : "social_pitch")}
          data-testid="rdd-new-btn"
        >
          <Plus size={16} /> New proposal
        </button>
      </div>

      {/* Search */}
      <div className="rdd-search-row">
        <div className="rdd-search">
          <Search size={15} />
          <input
            type="text"
            placeholder={activeTab === "new_business" ? "Search prospects by name…" : "Search agencies by name…"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="rdd-search-input"
          />
          {search && (
            <button onClick={() => setSearch("")} className="rdd-search-clear">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Existing-client search only on the Social tab */}
      {activeTab === "social" && search.trim() && (
        <div className="rdd-section">
          <div className="rdd-section-head">
            <h2>Matching agencies</h2>
            {searchingClients && <Loader2 size={13} className="rdd-spin" />}
          </div>
          {clients.length === 0 && !searchingClients ? (
            <p className="rdd-empty rdd-empty-tight">No agencies matched “{search}”.</p>
          ) : (
            <div className="rdd-client-grid">
              {clients.map((c) => (
                <button
                  key={c.id}
                  className="rdd-client-card"
                  onClick={() => onOpenBuilder(null, c)}
                  data-testid={`rdd-client-${c.id}`}
                >
                  <Building2 size={15} className="rdd-client-icon" />
                  <span className="rdd-client-name">{c.name}</span>
                  <span className="rdd-client-meta">
                    {c.proposal_count > 0 ? `${c.proposal_count} proposal${c.proposal_count > 1 ? "s" : ""}` : "New proposal"}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Existing proposals */}
      <div className="rdd-section">
        <div className="rdd-section-head">
          <h2>{search.trim() ? "Matching proposals" : "Your proposals"}</h2>
          <span className="rdd-section-count">{filteredProposals.length}</span>
        </div>

        {loading ? (
          <div className="rdd-loading"><Loader2 size={18} className="rdd-spin" /> Loading…</div>
        ) : filteredProposals.length === 0 ? (
          <p className="rdd-empty">
            {search.trim()
              ? `No proposals yet for “${search}”.`
              : "You haven't built any proposals yet. Click New proposal to start."}
          </p>
        ) : (
          <div className="rdd-prop-grid">
            {filteredProposals.map((p) => (
              <div key={p.id} className="rdd-prop-card" data-testid={`rdd-prop-${p.id}`}>
                <div className="rdd-prop-head">
                  <div className="rdd-prop-logo-placeholder"><Building2 size={18} /></div>
                  <div className="rdd-prop-meta">
                    <h3>{p.agency_name}</h3>
                    <div className="rdd-prop-stats">
                      <span><Eye size={11} /> {p.view_count} view{p.view_count === 1 ? "" : "s"}</span>
                      {p.last_viewed_at && <span><Clock size={11} /> last open {fmtRelative(p.last_viewed_at)}</span>}
                      <span>Created {fmtRelative(p.created_at)}</span>
                      <LinkExpiryChip daysRemaining={p.days_remaining} testId={`rdd-expiry-${p.id}`} />
                    </div>
                    {(p.collaborators?.length > 0 || p.created_by !== user?.id) && (
                      <div className="rdd-prop-share-chips">
                        {p.created_by !== user?.id && (
                          <span className="rdd-share-chip rdd-share-chip-owner" data-testid={`rdd-owner-chip-${p.id}`}>
                            <Crown size={10} /> {p.created_by_name}
                          </span>
                        )}
                        {p.collaborators?.length > 0 && (
                          <span className="rdd-share-chip" data-testid={`rdd-shared-chip-${p.id}`}>
                            <Users size={10} /> Shared with {p.collaborators.length}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="rdd-prop-prices">
                  <span><b>Go</b> {p.social_go_price}</span>
                  <span><b>Pro</b> {p.social_pro_price}</span>
                  <span><b>Narra</b> {p.narratour_price}</span>
                  <span><b>Pres</b> {p.presentour_price}</span>
                  {p.custom_lines?.length > 0 && (
                    <span className="rdd-prop-custom">+ {p.custom_lines.length} custom</span>
                  )}
                </div>

                <div className="rdd-prop-actions">
                  <button
                    className="rdd-action rdd-action-primary"
                    onClick={() => copyLink(p.slug)}
                    data-testid={`rdd-copy-${p.id}`}
                  >
                    <Copy size={13} /> Copy link
                  </button>
                  <RefreshLinkButton
                    proposalId={p.id}
                    compact
                    testId={`rdd-refresh-${p.id}`}
                    onExtended={(updated) => setProposals((arr) => arr.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)))}
                  />
                  <a
                    className="rdd-action"
                    href={`${window.location.origin}/proposal/${p.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink size={13} /> Open
                  </a>
                  {showAnalytics && (
                  <button
                    className="rdd-action"
                    onClick={() => setAnalyticsId(p.id)}
                    data-testid={`rdd-stats-${p.id}`}
                    title="Link analytics — opens, locations, devices, time on page"
                  >
                    <BarChart3 size={13} /> Stats
                  </button>
                  )}
                  <button
                    className="rdd-action"
                    onClick={() => setShareProposal(p)}
                    data-testid={`rdd-share-${p.id}`}
                    title="Share with another RD — collaborate or hand over"
                  >
                    <Users size={13} /> Share
                  </button>
                  <button
                    className="rdd-action"
                    onClick={() => onOpenBuilder(p.id, null)}
                    data-testid={`rdd-edit-${p.id}`}
                  >
                    <Pencil size={13} /> Edit
                  </button>
                  <button
                    className="rdd-action rdd-action-danger"
                    onClick={() => handleDelete(p.id)}
                    data-testid={`rdd-delete-${p.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {analyticsId && (
        <ProposalAnalyticsModal
          proposalId={analyticsId}
          onClose={() => setAnalyticsId(null)}
        />
      )}
      {shareProposal && (
        <ShareProposalModal
          proposal={shareProposal}
          currentUserId={user?.id}
          onClose={() => setShareProposal(null)}
          onChanged={load}
        />
      )}
      </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   CUSTOM EXAMPLE VIDEOS (social pitch)
   ───────────────────────────────────────────────────────────── */
function CustomExamplesCard({ useCustom, overrides, onToggle, onChange, hint }) {
  const [slots, setSlots] = useState([]);
  const [uploadingId, setUploadingId] = useState(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    axios.get(`${API}/api/example-videos`)
      .then((r) => setSlots(r.data.videos || []))
      .catch(() => {});
  }, []);

  // Several custom videos may share one override_id (e.g. three NarraTour demos) — order is upload order.
  const listFor = (id) => overrides.filter((o) => o.override_id === id);

  const handleFile = async (slot, file) => {
    if (!file) return;
    setUploadingId(slot.id);
    setProgress(0);
    try {
      const init = await axios.post(`${API}/api/example-videos/upload/init`);
      const uploadId = init.data.upload_id;
      const CHUNK = 4 * 1024 * 1024;
      const total = Math.ceil(file.size / CHUNK) || 1;
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK));
        fd.append("index", String(i));
        await axios.post(`${API}/api/example-videos/upload/${uploadId}/chunk`, fd);
        setProgress(Math.round(((i + 1) / (total + 1)) * 100));
      }
      const done = await axios.post(`${API}/api/example-videos/upload/${uploadId}/complete`, {
        filename: file.name,
        content_type: file.type || "video/mp4",
        poster_time: slot.poster_time ?? 0.5, // thumbnail frame — reels use 2.5s / 11.5s so the graphics are on screen
      });
      const n = listFor(slot.id).length + 1;
      onChange([...overrides, { override_id: slot.id, label: n > 1 ? `${slot.label} ${n}` : slot.label, url: done.data.url, poster_url: done.data.poster_url || "" }]);
      toast.success(`${slot.label} — custom ${slot.tab === "introductions" ? "intro" : "example"} ${n > 1 ? `#${n} ` : ""}added`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingId(null);
      setProgress(0);
    }
  };

  const removeOne = (ov) => onChange(overrides.filter((o) => o !== ov));
  const clearSlot = (slotId) => onChange(overrides.filter((o) => o.override_id !== slotId));

  const grouped = [
    { key: "introductions", title: "Intros", hint: "The short explainer that plays first for each product" },
    { key: "examples", title: "Examples", hint: "Real agent-branded videos — add as many per product as you like" },
  ];

  return (
    <div className="rdd-card" data-testid="rdd-custom-examples-card">
      <h3 className="rdd-card-title">Intro & example videos</h3>
      {hint && <p className="rdd-hint" data-testid="rdd-custom-examples-hint">{hint}</p>}
      <label className="rdd-check" data-testid="rdd-use-custom-examples">
        <input
          type="checkbox"
          checked={useCustom}
          onChange={(e) => onToggle(e.target.checked)}
        />
        <span>Use custom videos — replace the default intros and examples with agent-branded versions</span>
      </label>
      {!useCustom && (
        <p className="rdd-hint">The proposal will show the standard NicheCom intro and example videos.</p>
      )}
      {useCustom && (
        <div className="rdd-examples-list">
          {grouped.map((group) => {
            const groupSlots = slots.filter((s) => s.tab === group.key);
            if (groupSlots.length === 0) return null;
            return (
              <div key={group.key} className="rdd-examples-group" data-testid={`rdd-examples-group-${group.key}`}>
                <span className="rdd-examples-group-title">{group.title}</span>
                <span className="rdd-examples-group-hint">{group.hint}</span>
                {groupSlots.map((slot) => {
                  const list = listFor(slot.id);
                  const isUploading = uploadingId === slot.id;
                  return (
                    <div key={slot.id} className={`rdd-example-row ${list.length ? "has-custom" : ""}`} data-testid={`rdd-example-row-${slot.id}`}>
                      <div className="rdd-example-head">
                        <Film size={14} className="rdd-example-icon" />
                        <span className="rdd-example-label">{slot.label}</span>
                        {isUploading ? (
                          <span className="rdd-example-progress" data-testid={`rdd-example-progress-${slot.id}`}>
                            <Loader2 size={12} className="rdd-spin" /> {progress}%
                          </span>
                        ) : list.length ? (
                          <span className="rdd-example-status is-custom" data-testid={`rdd-example-status-${slot.id}`}>
                            <Check size={12} /> {list.length} custom
                            <button
                              type="button"
                              className="rdd-example-remove"
                              onClick={() => clearSlot(slot.id)}
                              title="Remove all and revert to default"
                              data-testid={`rdd-example-remove-${slot.id}`}
                            >
                              <X size={12} />
                            </button>
                          </span>
                        ) : (
                          <span className="rdd-example-status" data-testid={`rdd-example-status-${slot.id}`}>Default</span>
                        )}
                        {!isUploading && (
                          <label className="rdd-example-upload" data-testid={`rdd-example-upload-${slot.id}`} title={list.length ? "Add another video to this product" : "Upload a custom video"}>
                            {list.length ? <Plus size={12} /> : <Upload size={12} />} {list.length ? "Add another" : "Upload"}
                            <input
                              type="file"
                              accept="video/*"
                              style={{ display: "none" }}
                              onChange={(e) => { handleFile(slot, e.target.files?.[0]); e.target.value = ""; }}
                            />
                          </label>
                        )}
                      </div>
                      {list.length > 0 && (
                        <div className="rdd-example-chips" data-testid={`rdd-example-chips-${slot.id}`}>
                          {list.map((ov, i) => (
                            <span key={ov.url} className="rdd-example-chip" data-testid={`rdd-example-chip-${slot.id}-${i}`}>
                              {ov.poster_url ? <img src={ov.poster_url} alt="" /> : <Film size={11} />}
                              <span>{i + 1}. {ov.label || slot.label}</span>
                              <button
                                type="button"
                                onClick={() => removeOne(ov)}
                                title="Remove this video"
                                aria-label={`Remove ${ov.label || slot.label}`}
                                data-testid={`rdd-example-chip-remove-${slot.id}-${i}`}
                              >
                                <X size={11} />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
          <p className="rdd-hint">Uploads go straight to the NicheCom media bucket and only apply to this proposal. Products with more than one video get a swipe-through counter on the proposal.</p>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   SHARE / COLLABORATE MODAL
   ───────────────────────────────────────────────────────────── */
function ShareProposalModal({ proposal, currentUserId, onClose, onChanged }) {
  const [rdUsers, setRdUsers] = useState([]);
  const [collabs, setCollabs] = useState(proposal.collaborators || []);
  const [ownerId, setOwnerId] = useState(proposal.created_by);
  const [ownerName, setOwnerName] = useState(proposal.created_by_name);
  const [busy, setBusy] = useState(false);
  const [choosingOwner, setChoosingOwner] = useState(false);
  const isOwner = ownerId === currentUserId;

  useEffect(() => {
    axios.get(`${API}/api/proposals/rd-users`)
      .then((r) => setRdUsers(r.data.users || []))
      .catch(() => toast.error("Could not load RD list"));
  }, []);

  const byId = (id) => rdUsers.find((u) => u.id === id);

  const saveCollabs = async (next) => {
    setBusy(true);
    try {
      await axios.put(`${API}/api/proposals/${proposal.id}/collaborators`, { user_ids: next });
      setCollabs(next);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update collaborators");
    } finally {
      setBusy(false);
    }
  };

  const add = (uid) => !busy && saveCollabs([...collabs, uid]);
  const remove = (uid) => !busy && saveCollabs(collabs.filter((c) => c !== uid));

  const makeOwner = async (u) => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.put(`${API}/api/proposals/${proposal.id}/owner`, { user_id: u.id });
      setOwnerId(u.id);
      setOwnerName(u.name);
      setCollabs((prev) => {
        const next = prev.filter((c) => c !== u.id);
        if (!next.includes(currentUserId)) next.push(currentUserId);
        return next;
      });
      toast.success(`${u.name} is now the owner`);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reassign owner");
    } finally {
      setBusy(false);
    }
  };

  const leaveAs = async (newOwnerId) => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/proposals/${proposal.id}/leave`, newOwnerId ? { new_owner_id: newOwnerId } : {});
      toast.success("You've left the proposal");
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not leave proposal");
      setBusy(false);
    }
  };

  const handleLeave = () => {
    if (busy) return;
    if (isOwner) {
      setChoosingOwner(true);
    } else if (window.confirm("Remove yourself from this proposal?")) {
      leaveAs(null);
    }
  };

  const available = rdUsers.filter((u) => u.id !== ownerId && !collabs.includes(u.id));
  const onProposal = isOwner || collabs.includes(currentUserId);

  return (
    <div className="rdd-modal-overlay" onClick={onClose} data-testid="share-modal-overlay">
      <div className="rdd-share-modal" onClick={(e) => e.stopPropagation()} data-testid="share-modal">
        <div className="rdd-share-head">
          <h3><Users size={16} /> Share “{proposal.agency_name}”</h3>
          <button className="rdd-share-close" onClick={onClose} data-testid="share-modal-close"><X size={16} /></button>
        </div>

        <p className="rdd-share-tip" data-testid="share-owner-tip">
          <Info size={12} /> The owner's contact details are shown on the proposal.
        </p>

        {choosingOwner ? (
          <div className="rdd-share-choose" data-testid="share-choose-owner">
            <p className="rdd-share-choose-label">Choose who takes ownership before you leave:</p>
            <div className="rdd-share-pills">
              {collabs.map((cid) => {
                const u = byId(cid);
                return (
                  <button
                    key={cid}
                    className="rdd-pill rdd-pill-add"
                    disabled={busy}
                    onClick={() => leaveAs(cid)}
                    data-testid={`share-newowner-${cid}`}
                  >
                    <Crown size={11} /> {u?.name || "RD"}
                  </button>
                );
              })}
            </div>
            <button className="rdd-share-cancel" onClick={() => setChoosingOwner(false)} data-testid="share-choose-cancel">Cancel</button>
          </div>
        ) : (
          <>
            <span className="rdd-share-sub">On this proposal</span>
            <div className="rdd-share-pills" data-testid="share-on-proposal">
              <span className="rdd-pill rdd-pill-owner" data-testid="share-owner-row">
                <Crown size={11} /> {byId(ownerId)?.name || ownerName}
              </span>
              {collabs.map((cid) => {
                const u = byId(cid);
                return (
                  <span key={cid} className="rdd-pill rdd-pill-collab" data-testid={`share-collab-${cid}`}>
                    {u?.name || "RD"}{cid === currentUserId ? " (you)" : ""}
                    {isOwner && cid !== currentUserId && (
                      <button
                        className="rdd-pill-crown"
                        disabled={busy}
                        onClick={() => u && makeOwner(u)}
                        title="Make owner — their contact details will show on the proposal"
                        data-testid={`share-makeowner-${cid}`}
                      >
                        <Crown size={11} />
                      </button>
                    )}
                    <button
                      className="rdd-pill-x"
                      disabled={busy}
                      onClick={() => remove(cid)}
                      title="Remove from proposal"
                      data-testid={`share-remove-${cid}`}
                    >
                      <X size={11} />
                    </button>
                  </span>
                );
              })}
            </div>

            {available.length > 0 && (
              <>
                <span className="rdd-share-sub">Click a name to add</span>
                <div className="rdd-share-pills" data-testid="share-available">
                  {available.map((u) => (
                    <button
                      key={u.id}
                      className="rdd-pill rdd-pill-add"
                      disabled={busy}
                      onClick={() => add(u.id)}
                      data-testid={`share-add-${u.id}`}
                    >
                      <Plus size={11} /> {u.name}
                    </button>
                  ))}
                </div>
              </>
            )}

            {onProposal && (
              <button
                className="rdd-share-leave"
                disabled={busy || (isOwner && collabs.length === 0)}
                onClick={handleLeave}
                title={isOwner && collabs.length === 0 ? "Add a collaborator first" : "Remove yourself from this proposal"}
                data-testid="share-leave-btn"
              >
                <LogOut size={12} /> Leave proposal{isOwner ? " (choose a new owner)" : ""}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   BUILDER VIEW
   ───────────────────────────────────────────────────────────── */
function ProposalBuilder({ proposalId, presetClient, builderType, onClose }) {
  const isEdit = !!proposalId;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    proposal_type: builderType || "social_pitch",
    agency_name: presetClient?.name || "",
    agency_contact_name: "",
    agency_contact_email: "",
    client_id: presetClient?.id || null,
    wms_client_id: presetClient?.wms_client_id || null,
    logo_url: "",
    social_go_price: DEFAULTS.social_go_price,
    social_pro_price: DEFAULTS.social_pro_price,
    narratour_price: DEFAULTS.narratour_price,
    presentour_price: DEFAULTS.presentour_price,
    custom_lines: [],
    use_custom_examples: false,
    custom_examples: [],
    selected_products: [],
    bundles: [],
    show_individual_pricing: true,
    notes: "",
    small_print: DEFAULT_SMALL_PRINT,
    roi: null,
    layout_order: [],
  }));
  const [slug, setSlug] = useState(null);
  const [linkInfo, setLinkInfo] = useState(null); // { days_remaining, expires_at }
  const [createdId, setCreatedId] = useState(null);
  const [catalogue, setCatalogue] = useState({ products: [], by_category: {} });
  const newBundleId = () => `b${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

  // Fetch the product catalogue once on mount (new_business builder needs it)
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/proposals/products`);
        setCatalogue(r.data);
        // New-business proposals start with the core trio ticked: Photography, Floor Plan, EPC
        if (!isEdit && builderType === "new_business") {
          const byId = Object.fromEntries((r.data.products || []).map((p) => [p.id, p]));
          const starters = DEFAULT_NEW_BUSINESS_PRODUCTS.map((id) => byId[id]).filter(Boolean);
          setForm((f) => {
            if (f.selected_products.length) return f;
            return {
              ...f,
              selected_products: starters.map((p) => ({ product_id: p.id, nichecom_price: p.nichecom_price, vendor_price: null, photo_count: p.photo_count ?? null, bolt_on_discount: p.bolt_on_discount ?? null })),
              layout_order: [...starters.map((p) => productKey(p.id)), ...f.layout_order],
            };
          });
        }
      } catch (err) {
        // Non-fatal: builder still works without the catalogue, just without quick-add
        console.warn("Product catalogue fetch failed", err);
      }
    })();
  }, [isEdit, builderType]);

  // Load existing proposal in edit mode
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/proposals/${proposalId}`);
        const d = r.data;
        setForm({
          proposal_type: d.proposal_type || "social_pitch",
          agency_name: d.agency_name || "",
          agency_contact_name: d.agency_contact_name || "",
          agency_contact_email: d.agency_contact_email || "",
          client_id: d.client_id || null,
          wms_client_id: d.wms_client_id || null,
          logo_url: d.logo_url || "",
          social_go_price: d.social_go_price || DEFAULTS.social_go_price,
          social_pro_price: d.social_pro_price || DEFAULTS.social_pro_price,
          narratour_price: d.narratour_price || DEFAULTS.narratour_price,
          presentour_price: d.presentour_price || DEFAULTS.presentour_price,
          custom_lines: d.custom_lines || [],
          use_custom_examples: d.use_custom_examples || false,
          custom_examples: d.custom_examples || [],
          selected_products: d.selected_products || [],
          bundles: (d.bundles || []).map((b, i) => ({ ...b, id: b.id || `legacy${i}${Date.now().toString(36)}` })),
          show_individual_pricing: d.show_individual_pricing !== false,
          notes: d.notes || "",
          small_print: d.small_print ?? DEFAULT_SMALL_PRINT,
          roi: d.roi || null,
          layout_order: d.layout_order || [],
        });
        setSlug(d.slug);
        setLinkInfo({ days_remaining: d.days_remaining, expires_at: d.expires_at });
      } catch (err) {
        toast.error("Could not load proposal");
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, proposalId, onClose]);

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  // Products resolved against the catalogue (category needed for the recommended order)
  const selectedFull = useMemo(() => {
    const byId = Object.fromEntries((catalogue.products || []).map((p) => [p.id, p]));
    return form.selected_products.map((sp) => ({ ...sp, product: byId[sp.product_id] })).filter((sp) => sp.product);
  }, [form.selected_products, catalogue]);

  // Proposals saved before manual ordering existed start from today's automatic order.
  useEffect(() => {
    if (!catalogue.products?.length || form.layout_order.length || (!form.selected_products.length && !form.bundles.length)) return;
    setForm((f) => ({ ...f, layout_order: resolveLayoutOrder(selectedFull, f.bundles, []) }));
  }, [catalogue.products?.length, form.layout_order.length, selectedFull.length, form.bundles.length]);

  const addCustomLine = () => {
    setForm((f) => ({
      ...f,
      custom_lines: [...f.custom_lines, { label: "", price: "", note: "" }],
    }));
  };
  const updateCustomLine = (idx, key, val) => {
    setForm((f) => ({
      ...f,
      custom_lines: f.custom_lines.map((l, i) => (i === idx ? { ...l, [key]: val } : l)),
    }));
  };
  const removeCustomLine = (idx) => {
    setForm((f) => ({
      ...f,
      custom_lines: f.custom_lines.filter((_, i) => i !== idx),
    }));
  };

  // ── Selected products (new business) ──
  const toggleProduct = (product) => {
    setForm((f) => {
      const exists = f.selected_products.find((p) => p.product_id === product.id);
      if (exists) {
        return {
          ...f,
          selected_products: f.selected_products.filter((p) => p.product_id !== product.id),
          layout_order: f.layout_order.filter((k) => k !== productKey(product.id)),
        };
      }
      return {
        ...f,
        selected_products: [
          ...f.selected_products,
          { product_id: product.id, nichecom_price: product.nichecom_price, vendor_price: null, photo_count: product.photo_count ?? null, bolt_on_discount: product.bolt_on_discount ?? null },
        ],
        layout_order: [...f.layout_order, productKey(product.id)],
      };
    });
  };
  const removeProductById = (productId) => {
    const p = (catalogue.products || []).find((x) => x.id === productId) || { id: productId };
    toggleProduct(p);
  };
  const updateProductPrice = (productId, key, val) => {
    const num = val === "" ? null : Number(val);
    setForm((f) => ({
      ...f,
      selected_products: f.selected_products.map((p) =>
        p.product_id === productId ? { ...p, [key]: num } : p
      ),
    }));
  };

  // ── Bundles (new business) ──
  const addBundle = () => {
    const id = newBundleId();
    setForm((f) => ({
      ...f,
      bundles: [
        ...f.bundles,
        {
          id,
          name: "",
          product_ids: [],
          includes_epc_option: false,
          price_without_epc: 0,
          price_with_epc: null,
          vendor_price_without_epc: null,
          vendor_price_with_epc: null,
          epc_display: "split_rows",
          note: "",
        },
      ],
      layout_order: [...f.layout_order, `bundle:${id}`],
    }));
  };

  /**
   * Preset packages — Bronze / Silver / Gold.
   * Available as one-click adds. Auto-injects any missing products into
   * the proposal's selected_products list (so the bundle resolves to
   * real product names on the public page) and pushes a pre-priced bundle
   * with the EPC "+£40 if needed" displayed inline.
   */
  const PRESET_PACKAGES = useMemo(() => ([
    {
      key: "bronze",
      name: "Bronze",
      product_ids: ["floorplan-standard", "photos-standard"],
      price_without_epc: 90,
      price_with_epc: 130,
      note: "Floor plan + property photography. The essentials, sorted.",
    },
    {
      key: "silver",
      name: "Silver",
      product_ids: ["floorplan-standard", "photos-standard", "video-property"],
      price_without_epc: 130,
      price_with_epc: 170,
      note: "Adds property video on top of the Bronze essentials.",
    },
    {
      key: "gold",
      name: "Gold",
      product_ids: ["floorplan-standard", "photos-premium", "video-property", "drone-video"],
      price_without_epc: 240,
      price_with_epc: 280,
      note: "Premium photography, property video and drone. The full flagship package.",
    },
  ]), []);

  const addPresetBundle = (preset) => {
    // Resolve each preset product to its catalogue entry so we can grab nichecom_price
    const catalogueById = {};
    (catalogue.products || []).forEach((p) => { catalogueById[p.id] = p; });

    setForm((f) => {
      const existingIds = new Set(f.selected_products.map((sp) => sp.product_id));
      // Build the "missing" rows with the required nichecom_price filled in from the catalogue.
      // Backend rejects SelectedProduct without nichecom_price (Pydantic required float).
      const buildRow = (pid) => {
        const cat = catalogueById[pid];
        return {
          product_id: pid,
          nichecom_price: cat ? Number(cat.nichecom_price) : 0,
          vendor_price: null,
          photo_count: cat?.photo_count ?? null,
          bolt_on_discount: cat?.bolt_on_discount ?? null,
        };
      };

      const missing = preset.product_ids
        .filter((pid) => !existingIds.has(pid))
        .map(buildRow);

      // EPC is always relevant for the +£40 uplift — quietly add it too
      if (!existingIds.has("epc-residential")) {
        missing.push(buildRow("epc-residential"));
      }

      const id = newBundleId();
      return {
        ...f,
        selected_products: [...f.selected_products, ...missing],
        bundles: [
          ...f.bundles,
          {
            id,
            name: preset.name,
            product_ids: [...preset.product_ids],
            includes_epc_option: true,
            price_without_epc: preset.price_without_epc,
            price_with_epc: preset.price_with_epc,
            vendor_price_without_epc: null,
            vendor_price_with_epc: null,
            epc_display: "inline",
            note: preset.note,
            preset_key: preset.key,
          },
        ],
        layout_order: [...f.layout_order, ...missing.map((m) => productKey(m.product_id)), `bundle:${id}`],
      };
    });
    toast.success(`${preset.name} package added`);
  };
  const updateBundle = (idx, key, val) => {
    setForm((f) => ({
      ...f,
      bundles: f.bundles.map((b, i) => (i === idx ? { ...b, [key]: val } : b)),
    }));
  };
  const toggleBundleProduct = (idx, productId) => {
    setForm((f) => ({
      ...f,
      bundles: f.bundles.map((b, i) => {
        if (i !== idx) return b;
        const has = b.product_ids.includes(productId);
        return {
          ...b,
          product_ids: has
            ? b.product_ids.filter((id) => id !== productId)
            : [...b.product_ids, productId],
        };
      }),
    }));
  };
  const removeBundle = (idx) => {
    setForm((f) => ({
      ...f,
      bundles: f.bundles.filter((_, i) => i !== idx),
      layout_order: f.layout_order.filter((k) => k !== bundleKey(f.bundles[idx], idx)),
    }));
  };

  const handleSave = async () => {
    if (!form.agency_name.trim()) {
      toast.error("Agency name is required");
      return null;
    }
    // Strip empty custom lines silently
    const payload = {
      ...form,
      custom_lines: form.custom_lines.filter((l) => l.label.trim() && l.price.trim()),
      roi: form.roi ? sanitiseRoi(form.roi) : null,
      layout_order: form.proposal_type === "new_business" ? resolveLayoutOrder(selectedFull, form.bundles, form.layout_order) : [],
    };
    setSaving(true);
    try {
      if (isEdit) {
        const r = await axios.patch(`${API}/api/proposals/${proposalId}`, payload);
        toast.success("Saved");
        return r.data?.slug || slug;
      }
      const r = await axios.post(`${API}/api/proposals`, payload);
      setSlug(r.data.slug);
      setCreatedId(r.data.id);
      setLinkInfo({ days_remaining: r.data.days_remaining, expires_at: r.data.expires_at });
      toast.success("Proposal created");
      return r.data.slug;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Save first so the full page reflects every edit, then open it in a new tab
  const openFullPreview = async () => {
    const win = window.open("", "_blank");
    const savedSlug = await handleSave();
    if (savedSlug && win) win.location = `${window.location.origin}/proposal/${savedSlug}`;
    else win?.close();
  };

  const copyLink = () => {
    if (!slug) return;
    const url = `${window.location.origin}/proposal/${slug}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Link copied"),
      () => toast.error("Could not copy")
    );
  };

  if (loading) {
    return (
      <div className="rdd-wrap rdd-builder">
        <div className="rdd-loading"><Loader2 size={18} className="rdd-spin" /> Loading…</div>
      </div>
    );
  }

  return (
    <div className="rdd-wrap rdd-builder">
      <button className="rdd-back" onClick={onClose} data-testid="rdd-back-btn">
        <ArrowLeft size={14} /> Back to proposals
      </button>

      <div className="rdd-builder-head">
        <h1 className="rdd-title">{isEdit ? "Edit proposal" : "New proposal"}</h1>
        {slug && (
          <div className="rdd-share-bar" data-testid="rdd-share-bar">
            <LinkIcon size={13} />
            <span className="rdd-share-url">{window.location.origin}/proposal/{slug}</span>
            <button onClick={copyLink} className="rdd-share-copy" data-testid="rdd-share-copy">
              <Copy size={12} /> Copy
            </button>
            <a
              className="rdd-share-open"
              href={`${window.location.origin}/proposal/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="rdd-share-open"
            >
              <ExternalLink size={12} /> Preview
            </a>
            <LinkExpiryChip daysRemaining={linkInfo?.days_remaining} testId="rdd-share-expiry" />
            <RefreshLinkButton
              proposalId={proposalId || createdId}
              compact
              testId="rdd-share-refresh"
              onExtended={(updated) => setLinkInfo({ days_remaining: updated.days_remaining, expires_at: updated.expires_at })}
            />
          </div>
        )}
      </div>

      <div className={`rdd-builder-grid ${form.proposal_type === "new_business" ? "rdd-builder-grid-wide" : ""}`}>
        {/* Left: form */}
        <div className="rdd-form">
          <div className="rdd-card">
            <h3 className="rdd-card-title">Agency details</h3>
            <Field label="Agency name *">
              <input
                className="rdd-input"
                placeholder="e.g. Joe Bloggs Estate Agents"
                value={form.agency_name}
                onChange={(e) => setField("agency_name", e.target.value)}
                data-testid="rdd-agency-name"
              />
            </Field>
            {form.proposal_type === "new_business" && (
              <>
                <Field label="Contact name (optional)">
                  <input
                    className="rdd-input"
                    placeholder="e.g. Jane Walker"
                    value={form.agency_contact_name}
                    onChange={(e) => setField("agency_contact_name", e.target.value)}
                    data-testid="rdd-contact-name"
                  />
                </Field>
                <Field label="Contact email (optional)">
                  <input
                    className="rdd-input"
                    placeholder="jane@joebloggs.co.uk"
                    value={form.agency_contact_email}
                    onChange={(e) => setField("agency_contact_email", e.target.value)}
                    data-testid="rdd-contact-email"
                  />
                </Field>
              </>
            )}
            {/* Agency logo field parked for now (logo_url still stored/accepted by the API) */}
          </div>

          {form.proposal_type === "social_pitch" && (
            <div className="rdd-card">
              <h3 className="rdd-card-title">Standard pricing</h3>
              <div className="rdd-price-grid">
                <Field label="Social Go">
                  <input className="rdd-input" value={form.social_go_price}
                    onChange={(e) => setField("social_go_price", e.target.value)}
                    data-testid="rdd-price-go" />
                </Field>
                <Field label="Social Pro">
                  <input className="rdd-input" value={form.social_pro_price}
                    onChange={(e) => setField("social_pro_price", e.target.value)}
                    data-testid="rdd-price-pro" />
                </Field>
                <Field label="NarraTour add-on">
                  <input className="rdd-input" value={form.narratour_price}
                    onChange={(e) => setField("narratour_price", e.target.value)}
                    data-testid="rdd-price-narra" />
                </Field>
                <Field label="PresenTour add-on">
                  <input className="rdd-input" value={form.presentour_price}
                    onChange={(e) => setField("presentour_price", e.target.value)}
                    data-testid="rdd-price-pres" />
                </Field>
              </div>
              <p className="rdd-hint">Use whatever format reads best — "£50", "£50/mo", "+£15", "POA"…</p>
            </div>
          )}

          {(form.proposal_type === "social_pitch" || form.proposal_type === "new_business") && (
            <CustomExamplesCard
              useCustom={form.use_custom_examples}
              overrides={form.custom_examples}
              onToggle={(v) => setField("use_custom_examples", v)}
              onChange={(list) => setField("custom_examples", list)}
              hint={form.proposal_type === "new_business" ? "Shown in the Social Go / Social Pro carousel on the proposal's Social section." : undefined}
            />
          )}

          {form.proposal_type === "new_business" && (
            <ProductPicker
              catalogue={catalogue}
              selected={form.selected_products}
              onToggle={toggleProduct}
              onPriceChange={updateProductPrice}
            />
          )}

          {form.proposal_type === "new_business" && (
            <BundleBuilder
              catalogue={catalogue}
              bundles={form.bundles}
              selectedProducts={form.selected_products}
              presetPackages={PRESET_PACKAGES}
              onAddBundle={addBundle}
              onAddPreset={addPresetBundle}
              onUpdate={updateBundle}
              onToggleProduct={toggleBundleProduct}
              onRemove={removeBundle}
            />
          )}

          {form.proposal_type === "new_business" && (
            <RoiSettingsCard
              roi={form.roi}
              bundles={form.bundles}
              onChange={(roi) => setField("roi", roi)}
            />
          )}

          {form.proposal_type === "new_business" && (
            <div className="rdd-card">
              <h3 className="rdd-card-title">Display options</h3>
              <label className="rdd-check">
                <input
                  type="checkbox"
                  checked={form.show_individual_pricing}
                  onChange={(e) => setField("show_individual_pricing", e.target.checked)}
                  data-testid="rdd-show-individual"
                />
                <span>Show individual product pricing table on the prospect's page (in addition to bundles)</span>
              </label>
              <div className="rdd-field rdd-small-print-field">
                <span className="rdd-field-label">Small print <em className="rdd-field-sub">— shown under the pricing</em></span>
                <textarea
                  className="rdd-input rdd-textarea rdd-small-print"
                  rows={2}
                  value={form.small_print ?? ""}
                  onChange={(e) => setField("small_print", e.target.value)}
                  placeholder={DEFAULT_SMALL_PRINT}
                  data-testid="rdd-small-print"
                />
              </div>
            </div>
          )}

          {form.proposal_type === "social_pitch" && (
            <div className="rdd-card">
              <div className="rdd-card-head-row">
                <h3 className="rdd-card-title">Custom line items</h3>
                <button className="rdd-add-btn" onClick={addCustomLine} data-testid="rdd-add-line">
                  <Plus size={13} /> Add line
                </button>
              </div>
              {form.custom_lines.length === 0 ? (
                <p className="rdd-empty rdd-empty-tight">No custom lines yet. Add things like onboarding fees, drone shoots, podcast packages, anything bespoke.</p>
              ) : (
                <div className="rdd-lines">
                  {form.custom_lines.map((line, idx) => (
                    <div key={idx} className="rdd-line">
                      <input
                        className="rdd-input rdd-line-label"
                        placeholder="Label (e.g. Onboarding day)"
                        value={line.label}
                        onChange={(e) => updateCustomLine(idx, "label", e.target.value)}
                        data-testid={`rdd-line-label-${idx}`}
                      />
                      <input
                        className="rdd-input rdd-line-price"
                        placeholder="Price (e.g. £750)"
                        value={line.price}
                        onChange={(e) => updateCustomLine(idx, "price", e.target.value)}
                        data-testid={`rdd-line-price-${idx}`}
                      />
                      <button
                        className="rdd-line-remove"
                        onClick={() => removeCustomLine(idx)}
                        data-testid={`rdd-line-remove-${idx}`}
                        title="Remove line"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="rdd-card">
            <h3 className="rdd-card-title">Notes (optional)</h3>
            <textarea
              className="rdd-textarea"
              rows={4}
              placeholder="Anything you want to call out to the prospect — context on the offer, bundling notes, next steps…"
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              data-testid="rdd-notes"
            />
          </div>

          <div className="rdd-builder-actions">
            <button className="rdd-save-btn" onClick={handleSave} disabled={saving} data-testid="rdd-save-btn">
              {saving ? <><Loader2 size={14} className="rdd-spin" /> Saving</> : <><Check size={14} /> {isEdit ? "Save changes" : "Create proposal"}</>}
            </button>
          </div>
        </div>

        {/* Right: live preview (social pitch) or the drag-to-order panel (new business) */}
        {form.proposal_type === "social_pitch" && <PreviewCard form={form} />}
        {form.proposal_type === "new_business" && (
          <ProposalOrderPanel
            catalogue={catalogue}
            form={form}
            onChangeOrder={(keys) => setField("layout_order", keys)}
            onRemoveProduct={removeProductById}
            onRemoveBundle={removeBundle}
            onOpenPreview={openFullPreview}
            saving={saving}
          />
        )}
      </div>
    </div>
  );
}

// Empty number fields become their defaults so the backend validators never see "".
const sanitiseRoi = (roi) => {
  const num = (v, d) => (v === "" || v === null || v === undefined || Number.isNaN(Number(v)) ? d : Number(v));
  return {
    ...roi,
    annual_visits_default: Math.min(Math.max(Math.round(num(roi.annual_visits_default, 100)), 1), 10000),
    visits_max: Math.min(Math.max(Math.round(num(roi.visits_max, 10000)), 10), 10000),
    current_cost_per_visit: num(roi.current_cost_per_visit, 150),
    nichecom_cost_per_visit: roi.nichecom_cost_per_visit === "" || roi.nichecom_cost_per_visit === null ? null : num(roi.nichecom_cost_per_visit, null),
    uplift_pct: Math.min(Math.max(num(roi.uplift_pct, 20), 0), 500),
    sqft: { ...roi.sqft, rate: num(roi.sqft?.rate, 0.025), threshold: num(roi.sqft?.threshold, 2500), avg_sqft: num(roi.sqft?.avg_sqft, 1800) },
    floorplan: { ...roi.floorplan, price: num(roi.floorplan?.price, 80), share_pct: num(roi.floorplan?.share_pct, 100) },
    epc: { ...roi.epc, price: num(roi.epc?.price, 90), share_pct: num(roi.epc?.share_pct, 40) },
  };
};

function Field({ label, children }) {
  return (
    <label className="rdd-field">
      <span className="rdd-field-label">{label}</span>
      {children}
    </label>
  );
}

function PreviewCard({ form }) {
  return (
    <div className="rdd-preview" data-testid="rdd-preview">
      <span className="rdd-preview-label">Prospect sees</span>
      <div className="rdd-preview-card">
        <h3 className="rdd-preview-agency">{form.agency_name || "Agency name"}</h3>

        <table className="rdd-preview-table">
          <thead>
            <tr>
              <th></th>
              <th>Social Go</th>
              <th>Social Pro</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Base</td><td>{form.social_go_price}</td><td>{form.social_pro_price}</td></tr>
            <tr><td>NarraTour</td><td>—</td><td>{form.narratour_price}</td></tr>
            <tr><td>PresenTour</td><td>—</td><td>{form.presentour_price}</td></tr>
            {form.custom_lines.filter((l) => l.label || l.price).map((l, i) => (
              <tr key={i} className="rdd-preview-custom">
                <td>{l.label || "—"}</td>
                <td>—</td>
                <td>{l.price || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {form.notes && (
          <div className="rdd-preview-notes">{form.notes}</div>
        )}
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────
   PRODUCT PICKER (new business)
   ───────────────────────────────────────────────────────────── */
function ProductPicker({ catalogue, selected, onToggle, onPriceChange }) {
  const [bedroomMode, setBedroomMode] = useState({});
  const selectedById = useMemo(() => {
    const m = {};
    selected.forEach((s) => { m[s.product_id] = s; });
    return m;
  }, [selected]);

  if (!catalogue?.products?.length) {
    return (
      <div className="rdd-card">
        <h3 className="rdd-card-title">Products</h3>
        <p className="rdd-empty rdd-empty-tight">Loading product catalogue…</p>
      </div>
    );
  }

  return (
    <div className="rdd-card" data-testid="rdd-product-picker">
      <h3 className="rdd-card-title">
        Products <span className="rdd-card-title-meta">({selected.length} selected)</span>
      </h3>
      <p className="rdd-hint" style={{ marginTop: 0, marginBottom: 14 }}>
        Pick the services this agency is buying. Set their resale (vendor) price to show their margin on the proposal.
      </p>

      {Object.entries(catalogue.by_category).map(([category, products]) => {
        // Categories with a flat + by-bedrooms pricing group (3D Showcase) get a switch between the two
        const flat = products.filter((p) => p.pricing_group && p.pricing_mode === "flat");
        const banded = products.filter((p) => p.pricing_group && p.pricing_mode === "bedrooms");
        const grouped = flat.length > 0 && banded.length > 0;
        const byBedrooms = grouped && (bedroomMode[category] ?? banded.some((p) => selectedById[p.id]));
        const visible = grouped ? products.filter((p) => !p.pricing_group || (byBedrooms ? p.pricing_mode === "bedrooms" : p.pricing_mode === "flat")) : products;
        const switchMode = (on) => {
          setBedroomMode((m) => ({ ...m, [category]: on }));
          (on ? flat : banded).forEach((p) => { if (selectedById[p.id]) onToggle(p); });
        };
        return (
        <div key={category} className="rdd-cat">
          <div className="rdd-cat-head">
            {category}
            {grouped && (
              <RddToggle
                size="sm"
                checked={byBedrooms}
                onChange={switchMode}
                label={byBedrooms ? "Priced by bedrooms" : "One price for any property"}
                hint={byBedrooms ? "1–2 · 3–4 · 5–6 bedroom bands" : `Switch on to price by bedroom band`}
                testId={`rdd-bedroom-toggle-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              />
            )}
          </div>
          <div className="rdd-prod-list">
            {visible.map((p) => {
              const sel = selectedById[p.id];
              const isOn = !!sel;
              return (
                <div key={p.id} className={`rdd-prod ${isOn ? "is-on" : ""} ${p.addon_only ? "is-addon" : ""}`}>
                  <label className="rdd-prod-tap">
                    <input
                      type="checkbox"
                      checked={isOn}
                      onChange={() => onToggle(p)}
                      data-testid={`rdd-prod-${p.id}`}
                    />
                    <span className="rdd-prod-name">{p.name}</span>
                    {p.addon_only && <span className="rdd-prod-tag">Add-on</span>}
                    {p.photo_count != null && (
                      <span className="rdd-prod-tag rdd-prod-tag-photos" data-testid={`rdd-prod-photos-tag-${p.id}`}>
                        <Camera size={10} /> {isOn && sel.photo_count != null ? sel.photo_count : `${p.photo_count_prefix ? `${p.photo_count_prefix} ` : ""}${p.photo_count}`} {(isOn ? sel.photo_count : p.photo_count) === 1 ? "photo" : "photos"}
                      </span>
                    )}
                    {p.bolt_on_discount != null && (
                      <span className="rdd-prod-tag rdd-prod-tag-bolt" data-testid={`rdd-prod-bolt-tag-${p.id}`}>
                        £{Math.max(0, Number(isOn ? sel.nichecom_price : p.nichecom_price) - Number(isOn && sel.bolt_on_discount != null ? sel.bolt_on_discount : p.bolt_on_discount))} bolt-on
                      </span>
                    )}
                    <span className="rdd-prod-cost">£{p.nichecom_price}</span>
                  </label>
                  {isOn && (
                    <div className="rdd-prod-prices">
                      <div className="rdd-prod-price-cell">
                        <span className="rdd-prod-price-label">NicheCom</span>
                        <input
                          type="number"
                          className="rdd-input rdd-prod-price-input"
                          value={sel.nichecom_price ?? ""}
                          onChange={(e) => onPriceChange(p.id, "nichecom_price", e.target.value)}
                          data-testid={`rdd-prod-nc-${p.id}`}
                        />
                      </div>
                      <div className="rdd-prod-price-cell">
                        <span className="rdd-prod-price-label">Agent resale</span>
                        <input
                          type="number"
                          className="rdd-input rdd-prod-price-input"
                          placeholder="Optional"
                          value={sel.vendor_price ?? ""}
                          onChange={(e) => onPriceChange(p.id, "vendor_price", e.target.value)}
                          data-testid={`rdd-prod-vp-${p.id}`}
                        />
                      </div>
                      {p.photo_count != null && (
                        <div className="rdd-prod-price-cell">
                          <span className="rdd-prod-price-label">{p.photo_count_prefix ? "Photos (up to)" : "Photos included"}</span>
                          <input
                            type="number"
                            min="0"
                            className="rdd-input rdd-prod-price-input"
                            value={sel.photo_count ?? ""}
                            placeholder={String(p.photo_count)}
                            onChange={(e) => onPriceChange(p.id, "photo_count", e.target.value)}
                            data-testid={`rdd-prod-photos-${p.id}`}
                          />
                        </div>
                      )}
                      {p.bolt_on_discount != null && (
                        <div className="rdd-prod-price-cell">
                          <span className="rdd-prod-price-label">Bolt-on discount (off the price on an existing visit)</span>
                          <input
                            type="number"
                            min="0"
                            className="rdd-input rdd-prod-price-input"
                            value={sel.bolt_on_discount ?? ""}
                            placeholder={String(p.bolt_on_discount)}
                            onChange={(e) => onPriceChange(p.id, "bolt_on_discount", e.target.value)}
                            data-testid={`rdd-prod-bolt-${p.id}`}
                          />
                        </div>
                      )}
                      {sel.vendor_price > sel.nichecom_price && (
                        <div className="rdd-prod-margin">
                          +£{(sel.vendor_price - sel.nichecom_price).toFixed(0)} margin
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   BUNDLE BUILDER (new business)
   ───────────────────────────────────────────────────────────── */
function BundleBuilder({ catalogue, bundles, selectedProducts, presetPackages = [], onAddBundle, onAddPreset, onUpdate, onToggleProduct, onRemove }) {
  // Show all products the agency has selected as "buildable bundle items"
  const productById = useMemo(() => {
    const m = {};
    (catalogue.products || []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [catalogue.products]);

  const availableProducts = selectedProducts
    .map((sp) => productById[sp.product_id])
    .filter(Boolean);

  // Which preset packages are already added to this proposal (by name/key) — disable the button so we don't dupe
  const presetsInUse = new Set(bundles.map((b) => b.preset_key).filter(Boolean));

  return (
    <div className="rdd-card" data-testid="rdd-bundle-builder">
      <div className="rdd-card-head-row">
        <h3 className="rdd-card-title">
          Custom bundles <span className="rdd-card-title-meta">({bundles.length})</span>
        </h3>
        <button className="rdd-add-btn" onClick={onAddBundle} data-testid="rdd-add-bundle">
          <Plus size={13} /> Add bundle
        </button>
      </div>

      {/* Preset packages quick-add row (Bronze / Silver / Gold) */}
      {presetPackages.length > 0 && (
        <div className="rdd-presets" data-testid="rdd-preset-packages">
          <span className="rdd-presets-label">Quick add</span>
          {presetPackages.map((preset) => {
            const inUse = presetsInUse.has(preset.key);
            return (
              <button
                key={preset.key}
                type="button"
                className={`rdd-preset-btn rdd-preset-${preset.key} ${inUse ? "is-in-use" : ""}`}
                onClick={() => !inUse && onAddPreset?.(preset)}
                disabled={inUse}
                data-testid={`rdd-preset-${preset.key}`}
                title={inUse ? `${preset.name} already added` : `Add ${preset.name} package (£${preset.price_without_epc})`}
              >
                <span className="rdd-preset-dot" />
                {preset.name}
                <span className="rdd-preset-price">£{preset.price_without_epc}</span>
              </button>
            );
          })}
        </div>
      )}

      {bundles.length === 0 ? (
        <p className="rdd-empty rdd-empty-tight">
          No bundles yet. Use the Bronze / Silver / Gold quick-adds above, or build your own.
        </p>
      ) : (
        <div className="rdd-bundles">
          {bundles.map((b, idx) => (
            <BundleCard
              key={idx}
              bundle={b}
              idx={idx}
              availableProducts={availableProducts}
              onUpdate={onUpdate}
              onToggleProduct={onToggleProduct}
              onRemove={onRemove}
            />
          ))}
        </div>
      )}

      {availableProducts.length === 0 && bundles.length === 0 && (
        <p className="rdd-hint" style={{ marginTop: 12 }}>
          Tip: pick some products above first — bundles are built from the products you've selected.
        </p>
      )}
    </div>
  );
}

function BundleCard({ bundle, idx, availableProducts, onUpdate, onToggleProduct, onRemove }) {
  return (
    <div className="rdd-bundle" data-testid={`rdd-bundle-${idx}`}>
      <div className="rdd-bundle-head">
        <input
          className="rdd-input rdd-bundle-name"
          placeholder="Bundle name (e.g. Silver Package)"
          value={bundle.name}
          onChange={(e) => onUpdate(idx, "name", e.target.value)}
          data-testid={`rdd-bundle-name-${idx}`}
        />
        <button className="rdd-bundle-remove" onClick={() => onRemove(idx)} title="Remove bundle">
          <X size={14} />
        </button>
      </div>

      <div className="rdd-bundle-section">
        <div className="rdd-bundle-section-label">Included products</div>
        {availableProducts.length === 0 ? (
          <p className="rdd-hint">Add some products to the proposal first.</p>
        ) : (
          <div className="rdd-bundle-products">
            {availableProducts.map((p) => {
              const on = bundle.product_ids.includes(p.id);
              return (
                <button
                  key={p.id}
                  className={`rdd-bundle-prod ${on ? "is-on" : ""}`}
                  onClick={() => onToggleProduct(idx, p.id)}
                  type="button"
                  data-testid={`rdd-bundle-${idx}-prod-${p.id}`}
                >
                  {on && <Check size={11} />} {p.name}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="rdd-bundle-section">
        <label className="rdd-check">
          <input
            type="checkbox"
            checked={bundle.includes_epc_option}
            onChange={(e) => onUpdate(idx, "includes_epc_option", e.target.checked)}
            data-testid={`rdd-bundle-epc-toggle-${idx}`}
          />
          <span>Offer this bundle with <strong>and</strong> without EPC</span>
        </label>
      </div>

      <div className="rdd-bundle-price-grid">
        <Field label={bundle.includes_epc_option ? "Price (without EPC)" : "Price"}>
          <input
            type="number"
            className="rdd-input"
            placeholder="e.g. 180"
            value={bundle.price_without_epc || ""}
            onChange={(e) => onUpdate(idx, "price_without_epc", Number(e.target.value) || 0)}
            data-testid={`rdd-bundle-price-${idx}`}
          />
        </Field>
        <Field label="Agent resale (optional)">
          <input
            type="number"
            className="rdd-input"
            placeholder="Optional"
            value={bundle.vendor_price_without_epc ?? ""}
            onChange={(e) => onUpdate(idx, "vendor_price_without_epc", e.target.value === "" ? null : Number(e.target.value))}
            data-testid={`rdd-bundle-vp-${idx}`}
          />
        </Field>
        {bundle.includes_epc_option && (
          <>
            <Field label="Price (with EPC)">
              <input
                type="number"
                className="rdd-input"
                placeholder="e.g. 220"
                value={bundle.price_with_epc ?? ""}
                onChange={(e) => onUpdate(idx, "price_with_epc", e.target.value === "" ? null : Number(e.target.value))}
                data-testid={`rdd-bundle-price-epc-${idx}`}
              />
            </Field>
            <Field label="Agent resale (with EPC)">
              <input
                type="number"
                className="rdd-input"
                placeholder="Optional"
                value={bundle.vendor_price_with_epc ?? ""}
                onChange={(e) => onUpdate(idx, "vendor_price_with_epc", e.target.value === "" ? null : Number(e.target.value))}
                data-testid={`rdd-bundle-vp-epc-${idx}`}
              />
            </Field>
          </>
        )}
      </div>

      {bundle.includes_epc_option && (
        <div className="rdd-bundle-section">
          <div className="rdd-bundle-section-label">EPC display style</div>
          <div className="rdd-radio-row">
            {[
              { value: "split_rows", label: "Two separate rows" },
              { value: "inline", label: "Inline (+£X if EPC needed)" },
              { value: "toggle", label: "Toggle on the page" },
            ].map((opt) => (
              <label key={opt.value} className={`rdd-radio ${bundle.epc_display === opt.value ? "is-on" : ""}`}>
                <input
                  type="radio"
                  name={`epc-display-${idx}`}
                  checked={bundle.epc_display === opt.value}
                  onChange={() => onUpdate(idx, "epc_display", opt.value)}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
